<script setup lang="ts">
import { nextTick, ref } from "vue";
import { useBoard, type TaskRowData } from "../stores/board.js";
import { URGENCY_MIN, branchUrl, urgencyLabel, type Status } from "@shared/types.js";
import { formatHours } from "../lib/format.js";
import { toast } from "../lib/toast.js";
import { queuedTaskIds } from "../lib/claudeQueued.js";
import LockTag from "./LockTag.vue";
import StatusChip from "./StatusChip.vue";

const props = defineProps<{
  row: TaskRowData;
  dragging: boolean;
  dropInto: boolean;
  dropAfter: boolean;
  /** Hovering the middle of the parent this task already sits in: dropping
   *  promotes it out, so it must not look like the nest cue. */
  dropOut: boolean;
}>();
const board = useBoard();

const editing = ref(false);
const draft = ref("");
const input = ref<HTMLInputElement | null>(null);

/** Someone else is renaming this task, here or in their drawer. */
const titleHolder = () => board.lockHolder(props.row.task.id, "title");

async function beginEdit(): Promise<void> {
  const holder = titleHolder();
  if (holder) {
    toast(`${holder.displayName} is editing this title`);
    return;
  }
  draft.value = props.row.task.title;
  editing.value = true;
  board.claim(props.row.task.id, "title");
  await nextTick();
  input.value?.select();
}

function cancel(): void {
  editing.value = false;
  board.release(props.row.task.id, "title");
}

async function commit(): Promise<void> {
  if (!editing.value) return;
  editing.value = false;
  board.release(props.row.task.id, "title");
  const title = draft.value.trim();
  if (title && title !== props.row.task.title) await board.patchTask(props.row.task.id, { title });
}

/**
 * Only on a row that sets a repo or branch itself, but built from the
 * effective pair: a sub-task naming just a branch still needs its ancestor's
 * repo to link anywhere. A row that sets neither would only repeat its
 * parent's chip down the whole subtree.
 */
const repo = (): string | null => board.effective(props.row.task, "repo");
const branch = (): string | null => board.effective(props.row.task, "branch");
const link = (): string | null =>
  props.row.task.repo || props.row.task.branch ? branchUrl(repo(), branch()) : null;

/**
 * What the tree SORTS this row by: the task's own step, or the most pressing
 * thing still unfinished under it, whichever is higher (shared/rollup.ts).
 *
 * Not its own step alone. A parent is where a folded tree stops, so a dot that
 * ignored the subtree would hide the one urgent sub-task inside a quiet-looking
 * feature — and it would disagree with the order the rows are in.
 *
 * A finished task rolls up to 0, below the scale, and lands back on `low`:
 * finished work is not pressing, whatever it was on the way.
 */
/** Queued for Claude to start on its own (not Manual): an orange ring round the dot. */
const queued = (): boolean => queuedTaskIds.value.has(props.row.task.id);

const urgency = (): number => Math.max(URGENCY_MIN, props.row.rollup.urgency);

/** Borrowed from below rather than set here — worth saying in the tooltip,
 *  since the row's own slider in the drawer will read lower. */
const lifted = (): boolean => props.row.rollup.urgency > (props.row.task.urgency ?? URGENCY_MIN);

/** Assigned to whoever is signed in — the rows that are theirs to do. */
const mine = (): boolean => !!board.me && props.row.task.assignees.includes(board.me.id);

/**
 * Add a sub-task under this row, without going through the drawer.
 *
 * On every task row, not only the ones that already have children: breaking a
 * task down is how a leaf BECOMES a parent, and the drag-into-the-middle-band
 * gesture only moves a task that already exists. The new child lands last
 * (omitted afterId) and is selected, so the drawer is on it ready to be named —
 * which is also when it is saved. The parent is NOT opened: a folded row stays
 * folded, and the drawer is where the new one is seen.
 */
function addChild(): void {
  const task = board.addTask({ parentId: props.row.task.id });
  board.select(task.id);
}

/** Unsaved, so it has no title to show yet and no link to copy or drag. */
const draftRow = (): boolean => board.isDraft(props.row.task.id);
</script>

<template>
  <div
    class="row"
    :class="{
      selected: board.selectedId === row.task.id,
      mine: mine(),
      done: row.task.status === 'done',
      'dragover-into': dropInto,
      'dragover-below': dropAfter,
      'dragover-out': dropOut,
    }"
    :style="{ opacity: dragging ? 0.4 : 1, '--depth': row.depth }"
    :draggable="!draftRow()"
    @click="board.select(row.task.id)"
    @dblclick="beginEdit"
    @dragstart="$emit('dragstart', $event)"
    @dragover="$emit('dragover', $event)"
    @drop.prevent="$emit('drop')"
  >
    <button
      class="twisty"
      :class="{ open: row.expanded, leaf: !row.hasChildren }"
      @click.stop="board.toggle(row.task.id)"
    >▶</button>

    <span
      class="urgency-dot"
      :class="['u' + urgency(), { queued: queued() }]"
      :title="`urgency: ${urgencyLabel(urgency())}${lifted() ? ' (from a sub-task)' : ''}${queued() ? ' — queued for Claude to start on its own' : ''}`"
    ></span>

    <input
      v-if="editing"
      ref="input"
      v-model="draft"
      class="title-input"
      @blur="commit"
      @keydown.enter.prevent="commit"
      @keydown.esc.prevent="cancel"
      @click.stop
    />
    <!-- A draft has no title until somebody types one, and it is not saved
         until they do; the placeholder says which row they are naming. -->
    <span v-else-if="draftRow() && !row.task.title" class="title unnamed">New task…</span>
    <span v-else class="title" :title="row.task.title">{{ row.task.title }}</span>
    <LockTag v-if="!editing" :user="titleHolder()" />

    <a
      v-if="link()"
      class="branch"
      :href="link()!"
      target="_blank"
      rel="noreferrer"
      :title="`${repo()} — ${branch()}`"
      @click.stop
    >{{ branch() }}</a>

    <!-- Needs root: an admin's to do, never Claude's (off its queue). -->
    <span v-if="row.task.adminTask" class="admin-task" title="Admin task: needs a person with root">root</span>

    <span
      v-for="id in row.task.assignees"
      :key="id"
      class="avatar"
      style="width: 18px; height: 18px; font-size: 8px"
      :style="{ background: board.userColor(id) }"
      :title="board.userName(id)"
    >{{ board.userName(id).slice(0, 2) }}</span>

    <!-- Time on this task and everything under it. Hidden when nothing has been
         logged, so a board that does not track time never grows the column. -->
    <span
      v-if="row.rollup.spentHours > 0"
      class="spent"
      :title="row.hasChildren ? 'time logged across this subtree' : 'time logged'"
    >
      {{ formatHours(row.rollup.spentHours) }}
    </span>

    <template v-if="row.hasChildren">
      <span class="mini"><i :style="{ width: Math.round(row.rollup.percent * 100) + '%' }"></i></span>
      <span class="pct">{{ Math.round(row.rollup.percent * 100) }}%</span>
    </template>
    <!-- A parent carries a chip TOO, beside its rollup. Nothing finishes a
         parent but somebody saying so, so every row needs the control that
         says it; the bar answers "how far is what is under this", the chip
         "is this task itself finished". -->
    <StatusChip
      :status="row.task.status"
      @change="(s: Status) => board.setStatus(row.task.id, s)"
    />

    <!-- Nothing hangs under a task that does not exist yet. -->
    <button v-if="!draftRow()" class="add-child" title="Add a sub-task" @click.stop="addChild">+</button>
  </div>
</template>
